import moment from 'moment';

/**
 * The Condition model class
 */
export class Condition {
  /**
   * The field name
   */
  public FieldName: string;

  /**
   * The condition operator
   */
  public Operator: ConditionOperator;

  /**
   * The value
   */
  public Value: any;

  /**
   * The data type
   */
  public DataType: ConditionDataType;

  /**
   * A value indicating whether the condition should be ignored if the value is null or empty
   */
  public IgnoreIfValueNull: boolean;

  /**
   * Create a new Condition
   * @param fieldName The field name
   * @param operator The condition operator
   * @param value The value
   * @param ignoreIfValueNull A value indicating whether the condition should be ignored if the value is null or empty (defaults to false)
   */
  constructor(fieldName: string, operator: ConditionOperator, value: any, dataType: ConditionDataType, ignoreIfValueNull: boolean = false) {
    this.FieldName = fieldName;
    this.Operator = operator;
    this.Value = value;
    this.DataType = dataType;
    this.IgnoreIfValueNull = ignoreIfValueNull;
  }

  public IsIgnored() {
    if (this.IgnoreIfValueNull) {
      return this.Value === null || this.Value === "";
    } else {
      return false;
    }
  }

  public ToDataQuery() {
    switch (this.Operator) {
      case ConditionOperator.GreaterThan: return `${this.FieldName} gt ${this.ToDataQueryValue()}`;
      case ConditionOperator.GreaterThanOrEqualTo: return `${this.FieldName} ge ${this.ToDataQueryValue()}`;
      case ConditionOperator.LessThan: return `${this.FieldName} lt ${this.ToDataQueryValue()}`;
      case ConditionOperator.LessThanOrEqualTo: return `${this.FieldName} le ${this.ToDataQueryValue()}`;
      case ConditionOperator.EqualTo: return `${this.FieldName} eq ${this.ToDataQueryValue()}`;
      case ConditionOperator.NotEqualTo: return `${this.FieldName} ne ${this.ToDataQueryValue()}`;
      case ConditionOperator.Contains: return `indexof(${this.FieldName}, ${this.ToDataQueryValue()}) gt -1`;
      case ConditionOperator.Null: return `${this.FieldName} eq null`;
      case ConditionOperator.NotNull: return `${this.FieldName} ne null`;
      default: throw new Error(`Unhandled Operator: ${this.Operator}`);
    }
  }

  public ToDataQueryValue() {
    if (this.Value === null) {
      return "null";
    } else {
      switch (this.DataType) {
        case ConditionDataType.Bool: return this.Value ? "true" : "false";
        case ConditionDataType.Date: return `${moment(this.Value).format('YYYY-MM-DD')}Z`;
        case ConditionDataType.DateTime: return `${moment(this.Value).format('YYYY-MM-DDTHH:mm:ss')}Z`;
        case ConditionDataType.Decimal: return `${this.Value}`;
        case ConditionDataType.Guid: return `${this.Value}`;
        case ConditionDataType.Int: return `${this.Value}`;
        case ConditionDataType.String: return `'${encodeURIComponent((<string>this.Value).replace(/'/g, "''"))}'`;
        default: throw new Error(`Unhandled DataType: ${this.DataType}`);
      }
    }
  }
}

export enum ConditionOperator {
  GreaterThan = 1,
  GreaterThanOrEqualTo = 2,
  LessThan = 3,
  LessThanOrEqualTo = 4,
  EqualTo = 5,
  NotEqualTo = 6,
  Contains = 7,
  Null = 8,
  NotNull = 9,
}

export enum ConditionDataType {
  Bool = 1,
  Date = 2,
  DateTime = 3,
  Decimal = 4,
  Guid = 5,
  Int = 6,
  String = 7
}
